import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

/**
 * One column of a grouping table. `sortValue` doubles as the CSV/clipboard
 * value unless `csvValue` overrides it, so a grouping declares its shape once
 * and gets sorting + export for free.
 */
export interface GroupColumn<T> {
  key: string;
  label: string;
  numeric?: boolean;
  /** Header tint — used for the pipeline-status funnel columns. */
  headerColor?: string;
  width?: number | string;
  sortValue: (row: T) => number | string;
  render?: (row: T) => React.ReactNode;
  csvValue?: (row: T) => string;
}

export interface GroupTableProps<T> {
  columns: GroupColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  defaultSortKey: string;
  defaultSortDir?: "asc" | "desc";
  maxHeight?: number | string;
  emptyMessage?: string;
}

/** Sortable, sticky-header table shared by all four referral groupings. */
function GroupTable<T>({
  columns,
  rows,
  getRowKey,
  defaultSortKey,
  defaultSortDir = "desc",
  maxHeight = "62vh",
  emptyMessage = "Nothing to show for this filter.",
}: GroupTableProps<T>) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultSortDir);

  const active = columns.find((c) => c.key === sortKey);

  const sorted = useMemo(() => {
    if (!active) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = active.sortValue(a);
      const bv = active.sortValue(b);
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av)
          .toLowerCase()
          .localeCompare(String(bv).toLowerCase());
        return sortDir === "asc" ? cmp : -cmp;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [rows, active, sortDir]);

  const handleSort = (column: GroupColumn<T>) => {
    if (sortKey === column.key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(column.key);
      setSortDir(column.numeric ? "desc" : "asc");
    }
  };

  const defaultRender = (column: GroupColumn<T>, row: T) => {
    const value = column.sortValue(row);
    if (typeof value === "number") {
      return value === 0 ? (
        <span style={{ color: "#c5c5cc" }}>0</span>
      ) : (
        value.toLocaleString()
      );
    }
    return value;
  };

  return (
    <TableContainer sx={{ overflow: "auto", maxHeight }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.numeric ? "right" : "left"}
                sortDirection={sortKey === column.key ? sortDir : false}
                sx={{
                  backgroundColor: "#fff",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  color: column.headerColor,
                  width: column.width,
                }}
              >
                <TableSortLabel
                  active={sortKey === column.key}
                  direction={sortKey === column.key ? sortDir : "asc"}
                  onClick={() => handleSort(column)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography variant="body2" sx={{ color: "#8a8a93", py: 2 }}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {sorted.map((row) => (
            <TableRow key={getRowKey(row)} hover>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.numeric ? "right" : "left"}
                  sx={{ width: column.width }}
                >
                  {column.render
                    ? column.render(row)
                    : defaultRender(column, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default GroupTable;
